// Phase 8 Plan 03 Task 1 — per-template walker for the `summary-checklist` template.
//
// Pure TS — no React, no DOM. Server-friendly: Phase 10 AI prompt-gen + Phase 13 SSR
// re-import from here. Hand-written per RESEARCH §"Pattern 2 Option A".
//
// configSchema source mirrored verbatim (../../schemas/widget-templates/summary-checklist.ts):
//   items: ReadonlyArray<SummaryChecklistItem>
//   source: SummaryChecklistSource (discriminated by `type`: widget | selector | static)
//   viewMode: 'title' | 'snippet' | 'full' | 'expanded'
//   groupBy: 'category' | 'none'
//
// Walker output shape (kind:'group' root): four top-level children — items array,
// source group (with type discriminator leaf + optional siblings), viewMode enum
// leaf, groupBy enum leaf. The source group is emitted as a kind:'group' with the
// discriminator leaf + the two optional siblings for the 'widget' and 'selector'
// variants — Phase 8 renders the appropriate sibling based on the discriminator
// value at runtime; Phase 9 may swap this to a discriminated descriptor.

import type { FieldDescriptor } from "../descriptors.js";

export function walkSummaryChecklistConfig(configPath: string): FieldDescriptor {
  return {
    kind: "group",
    name: "config",
    path: configPath,
    children: [
      {
        kind: "array",
        name: "items",
        path: `${configPath}.items`,
        itemDescriptor: {
          kind: "group",
          name: "item",
          path: `${configPath}.items.*`,
          children: [
            {
              kind: "leaf",
              name: "id",
              path: `${configPath}.items.*.id`,
              type: "string",
              required: true,
            },
            {
              kind: "leaf",
              name: "title",
              path: `${configPath}.items.*.title`,
              type: "string",
              required: true,
            },
            {
              kind: "leaf",
              name: "description",
              path: `${configPath}.items.*.description`,
              type: "string",
              required: false,
            },
            {
              kind: "leaf",
              name: "groupId",
              path: `${configPath}.items.*.groupId`,
              type: "string",
              required: false,
            },
            {
              kind: "array",
              name: "subItems",
              path: `${configPath}.items.*.subItems`,
              itemDescriptor: {
                kind: "group",
                name: "subItem",
                path: `${configPath}.items.*.subItems.*`,
                children: [
                  {
                    kind: "leaf",
                    name: "id",
                    path: `${configPath}.items.*.subItems.*.id`,
                    type: "string",
                    required: true,
                  },
                  {
                    kind: "leaf",
                    name: "title",
                    path: `${configPath}.items.*.subItems.*.title`,
                    type: "string",
                    required: true,
                  },
                ],
              },
            },
            // visibleWhen lives on the descriptor's `visibleWhen?` slot for the
            // renderer; we expose it as a kind:'group' so the author UI can edit
            // sourceWidgetId + anyOf inline.
            {
              kind: "group",
              name: "visibleWhen",
              path: `${configPath}.items.*.visibleWhen`,
              children: [
                {
                  kind: "leaf",
                  name: "sourceWidgetId",
                  path: `${configPath}.items.*.visibleWhen.sourceWidgetId`,
                  type: "string",
                  required: true,
                },
                {
                  kind: "array",
                  name: "anyOf",
                  path: `${configPath}.items.*.visibleWhen.anyOf`,
                  itemDescriptor: {
                    kind: "leaf",
                    name: "optionId",
                    path: `${configPath}.items.*.visibleWhen.anyOf.*`,
                    type: "string",
                    required: true,
                  },
                },
              ],
            },
          ],
        },
      },
      {
        kind: "group",
        name: "source",
        path: `${configPath}.source`,
        children: [
          {
            kind: "leaf",
            name: "type",
            path: `${configPath}.source.type`,
            type: "enum",
            required: true,
            enumValues: [
              { id: "widget", label: "Widget" },
              { id: "selector", label: "Selector" },
              { id: "static", label: "Static" },
            ],
          },
          // Discriminated siblings — required at runtime per chosen `type`.
          // We emit both with required: false; the renderer (Plans 07-10) uses
          // useWatch on source.type and shows the appropriate sibling.
          {
            kind: "leaf",
            name: "sourceWidgetId",
            path: `${configPath}.source.sourceWidgetId`,
            type: "string",
            required: false,
          },
          {
            kind: "leaf",
            name: "pathInState",
            path: `${configPath}.source.pathInState`,
            type: "string",
            required: false,
          },
        ],
      },
      {
        kind: "leaf",
        name: "viewMode",
        path: `${configPath}.viewMode`,
        type: "enum",
        required: true,
        enumValues: [
          { id: "title", label: "Title" },
          { id: "snippet", label: "Snippet" },
          { id: "full", label: "Full" },
          { id: "expanded", label: "Expanded" },
        ],
      },
      {
        kind: "leaf",
        name: "groupBy",
        path: `${configPath}.groupBy`,
        type: "enum",
        required: true,
        enumValues: [
          { id: "category", label: "Category" },
          { id: "none", label: "None" },
        ],
      },
    ],
  };
}
